import { useState, type FormEvent } from 'react';
import { api, type EventTeam } from '../lib/api.ts';

/** Regie-Whitelist. Nur der Admin sieht und aendert diesen Block. */
export function TeamPanel({
  slug,
  team,
  onChange,
}: {
  slug: string;
  team: EventTeam;
  onChange: (team: EventTeam) => void;
}) {
  const [entry, setEntry] = useState('');
  const [deputy, setDeputy] = useState(team.deputyEmail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (patch: { directors?: string[]; deputyEmail?: string | null }) => {
    setError(null);
    setBusy(true);
    try {
      const r = await api.updateTeam(slug, patch);
      onChange(r.team);
      setDeputy(r.team.deputyEmail ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const mail = entry.trim().toLowerCase();
    if (!mail) return;
    if (team.directors.includes(mail)) {
      setError('Steht schon auf der Liste.');
      return;
    }
    await save({ directors: [...team.directors, mail] });
    setEntry('');
  };

  return (
    <div className="card" style={{ marginBottom: 22 }}>
      <div className="card__head">
        <h3>Wer darf Regie führen?</h3>
      </div>
      <div className="card__body stack gap-16">
        <p className="hint">
          Nur diese Adressen können sich als Veranstalter anmelden. Admin und Vize stehen immer drin und lassen sich
          nicht aus der Liste entfernen.
        </p>

        <ul className="teamlist">
          <li>
            <span className="teamlist__mail">{team.adminEmail}</span>
            <span className="tag tag--ok">Admin</span>
          </li>
          {team.deputyEmail && (
            <li>
              <span className="teamlist__mail">{team.deputyEmail}</span>
              <span className="tag tag--soon">Vize</span>
              <button type="button" disabled={busy} onClick={() => void save({ deputyEmail: null })} title="Vize entfernen">
                ×
              </button>
            </li>
          )}
          {team.directors.map((mail) => (
            <li key={mail}>
              <span className="teamlist__mail">{mail}</span>
              <span className="tag">Regie</span>
              <button
                type="button"
                disabled={busy}
                title="Von der Liste nehmen"
                onClick={() => void save({ directors: team.directors.filter((d) => d !== mail) })}
              >
                ×
              </button>
            </li>
          ))}
          {!team.deputyEmail && team.directors.length === 0 && (
            <li className="empty">Außer dir darf noch niemand Regie führen.</li>
          )}
        </ul>

        <form className="form-inline" onSubmit={add}>
          <input
            className="input"
            type="email"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            placeholder="person@verein.de"
            aria-label="E-Mail für die Regie-Liste"
          />
          <button className="btn btn--sm" type="submit" disabled={busy || !entry.trim()}>
            Freischalten
          </button>
        </form>

        {!team.deputyEmail && (
          <form
            className="form-inline"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ deputyEmail: deputy.trim() || null });
            }}
          >
            <input
              className="input"
              type="email"
              value={deputy}
              onChange={(e) => setDeputy(e.target.value)}
              placeholder="Vize-Adresse nachtragen"
              aria-label="Vize-E-Mail"
            />
            <button className="btn btn--sm btn--quiet" type="submit" disabled={busy || !deputy.trim()}>
              Als Vize
            </button>
          </form>
        )}

        {error && <div className="notice">{error}</div>}
        <p className="hint">
          Wer hier steht, braucht ein eigenes Konto mit genau dieser Adresse. Ohne Konto bleibt nur die
          Veranstalter-PIN.
        </p>
      </div>
    </div>
  );
}
